"use client";

import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Globe, Activity, ArrowUpRight, ArrowUp, ArrowDown, Zap, Calculator, Server, MapPinned, Gauge, WalletCards } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";

import NodeDisplay from "@/components/NodeDisplay";
import { formatBytes } from "@/utils/unitHelper";
import { normalizeCurrency } from "@/utils/assetSummary";
import { useLiveData } from "@/contexts/LiveDataContext";
import { useNodeList } from "@/contexts/NodeListContext";
import { usePublicInfo } from "@/contexts/PublicInfoContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAssetSummary } from "@/contexts/AssetSummaryContext";
import Loading from "@/components/loading";
import { CurrentTimeCard } from "@/components/CurrentTimeCard";
import { Callouts } from "@/components/DashboardCallouts";
import Flag from "@/components/Flag";

// Intelligent speed formatting function
const formatSpeed = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B/s";
  const units = ["B/s", "KB/s", "MB/s", "GB/s", "TB/s"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const size = bytes / Math.pow(1024, i);

  let decimals = 2;
  if (i >= 3) decimals = 1;
  if (i <= 1) decimals = 0;
  if (size >= 100) decimals = 0;

  return `${size.toFixed(decimals)} ${units[i]}`;
};

const formatSpeedShort = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0";
  const units = ["B", "K", "M", "G", "T"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const size = bytes / Math.pow(1024, i);
  const decimals = size >= 100 || i <= 1 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(decimals)}${units[i]}`;
};

type DashboardMetrics = {
  onlineCount: number;
  totalCount: number;
  offlineCount: number;
  onlineNodes: any[];
  offlineNodes: any[];
  regionCounts: Array<{ region: string; count: number }>;
  activeRegionCount: number;
  trafficUp: number;
  trafficDown: number;
  speedUp: number;
  speedDown: number;
  speedTopNodes: Array<{ uuid: string; name: string; up: number; down: number; total: number }>;
  assetTotal: number;
  assetCount: number;
};

function makeDashboardMetrics(nodeList: any[] | null, liveData: any, assetTotal: number, assetCount: number): DashboardMetrics {
  const nodes = nodeList || [];
  const onlineIds: string[] = liveData?.data?.online || [];
  const onlineSet = new Set(onlineIds);
  const data = liveData?.data?.data || {};
  const onlineNodes = nodes.filter((n) => onlineSet.has(n.uuid));
  const offlineNodes = nodes.filter((n) => !onlineSet.has(n.uuid));
  const regionMap = new Map<string, number>();
  for (const n of onlineNodes) {
    const region = String(n.region || "其他").trim() || "其他";
    regionMap.set(region, (regionMap.get(region) || 0) + 1);
  }
  const regionCounts = Array.from(regionMap.entries())
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count || a.region.localeCompare(b.region));

  const liveEntries = Object.entries<any>(data).filter(([uuid]) => onlineSet.has(uuid));
  const trafficUp = liveEntries.reduce((acc, [, node]) => acc + (node?.network?.totalUp || 0), 0);
  const trafficDown = liveEntries.reduce((acc, [, node]) => acc + (node?.network?.totalDown || 0), 0);
  const speedUp = liveEntries.reduce((acc, [, node]) => acc + (node?.network?.up || 0), 0);
  const speedDown = liveEntries.reduce((acc, [, node]) => acc + (node?.network?.down || 0), 0);
  const nameByUuid = new Map(nodes.map((n) => [n.uuid, n.name]));
  const speedTopNodes = liveEntries
    .map(([uuid, node]) => {
      const up = node?.network?.up || 0;
      const down = node?.network?.down || 0;
      return { uuid, name: nameByUuid.get(uuid) || uuid, up, down, total: up + down };
    })
    .filter((n) => n.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  return {
    onlineCount: onlineNodes.length,
    totalCount: nodes.length,
    offlineCount: offlineNodes.length,
    onlineNodes,
    offlineNodes,
    regionCounts,
    activeRegionCount: regionCounts.length,
    trafficUp,
    trafficDown,
    speedUp,
    speedDown,
    speedTopNodes,
    assetTotal,
    assetCount,
  };
}

export default function DashboardContent() {
  const [t] = useTranslation();
  const { live_data } = useLiveData();
  const { publicInfo } = usePublicInfo();
  const { themeConfig, statusCardsVisibility } = useTheme();
  const { summary: sharedAssetSummary } = useAssetSummary();
  const [activeCard, setActiveCard] = React.useState<string | null>(null);
  const [nodeScope, setNodeScope] = React.useState<NodeScope>({ kind: 'all' });
  
  useEffect(() => {
    if (publicInfo?.sitename) document.title = publicInfo.sitename;
  }, [publicInfo?.sitename]);
  
  const { nodeList, isLoading, error, refresh } = useNodeList();
  const [fallbackAssetTotalCNY, setFallbackAssetTotalCNY] = React.useState(0);

  useEffect(() => {
    let cancelled = false;
    async function run(){
      const nodes = nodeList || [];
      const online = new Set(live_data?.data?.online || []);
      if (!nodes.length || !online.size) { if(!cancelled) setFallbackAssetTotalCNY(0); return; }
      const rateCache = new Map<string, number>();
      async function rate(curr: string){
        if (curr === "CNY") return 1;
        if (rateCache.has(curr)) return rateCache.get(curr)!;
        try {
          const r = await fetch(`https://open.er-api.com/v6/latest/${curr}`);
          const j = await r.json();
          const v = Number(j?.rates?.CNY) || 1;
          rateCache.set(curr, v);
          return v;
        } catch {
          rateCache.set(curr, 1);
          return 1;
        }
      }
      let total = 0;
      for (const n of nodes) {
        if (!online.has(n.uuid)) continue;
        const raw = Number(n.price);
        const price = raw === -1 ? 0 : (Number.isFinite(raw) && raw > 0 ? raw : 0);
        total += price * await rate(normalizeCurrency(n.currency || "CNY"));
      }
      if(!cancelled) setFallbackAssetTotalCNY(total);
    }
    run();
    return () => { cancelled = true; };
  }, [nodeList, live_data?.data?.online]);

  const assetTotal = Number(sharedAssetSummary.count > 0 ? sharedAssetSummary.totalValueCNY : (fallbackAssetTotalCNY || 0));
  const metrics = React.useMemo(
    () => makeDashboardMetrics(nodeList, live_data, assetTotal, sharedAssetSummary.count || 0),
    [nodeList, live_data, assetTotal, sharedAssetSummary.count]
  );

  const statusCards: TopCardConfig[] = [
    {
      key: "currentTime",
      variant: "time",
      title: t("current_time"),
      icon: <Clock className="h-4 w-4 text-muted-foreground" />,
      value: <CurrentTimeCard timeZone={themeConfig.selectedTimeZone} />,
      visible: statusCardsVisibility.currentTime,
      detail: <TimeDetail />,
    },
    {
      key: "currentOnline",
      variant: "online",
      title: t("current_online"),
      icon: <Activity className="h-4 w-4 text-muted-foreground" />,
      value: `${metrics.onlineCount} / ${metrics.totalCount}`,
      visible: statusCardsVisibility.currentOnline,
      detail: <OnlineDetail metrics={metrics} nodeScope={nodeScope} onScopeChange={setNodeScope} />,
    },
    {
      key: "regionOverview",
      variant: "region",
      title: t("region_overview"),
      icon: <Globe className="h-4 w-4 text-muted-foreground" />,
      value: metrics.activeRegionCount,
      visible: statusCardsVisibility.regionOverview,
      detail: <RegionDetail metrics={metrics} nodeScope={nodeScope} onScopeChange={setNodeScope} />,
    },
    {
      key: "trafficOverview",
      variant: "traffic",
      title: t("traffic_overview"),
      icon: <ArrowUpRight className="h-4 w-4 text-muted-foreground" />,
      value: <TrafficPairMini up={formatBytes(metrics.trafficUp)} down={formatBytes(metrics.trafficDown)} />,
      visible: statusCardsVisibility.trafficOverview,
      detail: <TrafficDetail metrics={metrics} />,
    },
    {
      key: "networkSpeed",
      variant: "speed",
      title: t("network_speed"),
      icon: <Zap className="h-4 w-4 text-muted-foreground" />,
      value: <NetworkSpeedRings up={metrics.speedUp} down={metrics.speedDown} />,
      visible: statusCardsVisibility.networkSpeed,
      detail: <SpeedDetail metrics={metrics} />,
    },
    {
      key: "assetOverview",
      variant: "asset",
      title: t('asset_overview', { defaultValue: '资产统计' }),
      icon: <Calculator className="h-4 w-4 text-muted-foreground" />,
      value: `¥ ${assetTotal.toFixed(2)}`,
      visible: (statusCardsVisibility as any).assetOverview ?? true,
      detail: <AssetDetail metrics={metrics} />,
    },
  ];

  const visibleStatusCards = statusCards.filter((card) => card.visible);
  const activeConfig = visibleStatusCards.find((card) => card.key === activeCard) || null;

  const displayNodes = React.useMemo(() => {
    const nodes = nodeList || [];
    const onlineSet = new Set(live_data?.data?.online || []);
    if (nodeScope.kind === 'online') return nodes.filter((n) => onlineSet.has(n.uuid));
    if (nodeScope.kind === 'offline') return nodes.filter((n) => !onlineSet.has(n.uuid));
    if (nodeScope.kind === 'region') return nodes.filter((n) => String(n.region || '其他') === nodeScope.region);
    return nodes;
  }, [nodeList, live_data?.data?.online, nodeScope]);

  const scopeTitle = React.useMemo(() => {
    if (nodeScope.kind === 'online') return `在线机器 · ${displayNodes.length}`;
    if (nodeScope.kind === 'offline') return `离线机器 · ${displayNodes.length}`;
    if (nodeScope.kind === 'region') return `${nodeScope.region || '地区'} · ${displayNodes.length}`;
    return '';
  }, [nodeScope, displayNodes.length]);

  useEffect(() => {
    const interval = setInterval(() => refresh(), 5000);
    return () => clearInterval(interval);
  }, [refresh]);

  if (isLoading) return <Loading />;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="container mx-auto px-4 space-y-4">
      <Callouts />

      <div className="flex flex-col gap-4">
        <div className={`grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${visibleStatusCards.length >= 6 ? 'xl:grid-cols-6' : visibleStatusCards.length === 5 ? 'xl:grid-cols-5' : visibleStatusCards.length === 4 ? 'xl:grid-cols-4' : visibleStatusCards.length === 3 ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}>
          {visibleStatusCards.map((card) => (
            <TopCard
              key={card.key}
              cardKey={card.key}
              variant={card.variant}
              title={card.title}
              value={card.value}
              icon={card.icon}
              active={activeCard === card.key}
              onClick={() => setActiveCard((current) => current === card.key ? null : card.key)}
            />
          ))}
        </div>

        {activeConfig ? (
          <TopCardDetailPanel title={activeConfig.title} icon={activeConfig.icon} onClose={() => setActiveCard(null)}>
            {activeConfig.detail}
          </TopCardDetailPanel>
        ) : null}
      </div>

      {nodeScope.kind !== 'all' ? (
        <div className="ds-node-scope-bar">
          <span>当前筛选：<strong>{scopeTitle}</strong></span>
          <button type="button" onClick={() => setNodeScope({ kind: 'all' })}>显示全部</button>
        </div>
      ) : null}

      <Suspense fallback={<div className="p-4">Loading nodes...</div>}>
        <NodeDisplay
          nodes={displayNodes}
          liveData={live_data?.data ?? { online: [], data: {} }}
        />
      </Suspense>
    </div>
  );
}

type NodeScope = { kind: 'all' } | { kind: 'online' } | { kind: 'offline' } | { kind: 'region'; region: string };

type TopCardVariant = 'time' | 'online' | 'region' | 'traffic' | 'speed' | 'asset';
type TopCardConfig = {
  key: string;
  variant: TopCardVariant;
  title: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  visible: boolean;
  detail: React.ReactNode;
};

type TopCardProps = {
  cardKey: string;
  variant: TopCardVariant;
  title: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
};

const TopCard: React.FC<TopCardProps> = ({ cardKey, variant, title, value, icon, active, onClick }) => {
  let body: React.ReactNode = <div className="ds-dashboard-topcard-value">{value}</div>;

  if (variant === 'time') {
    body = (
      <div className="ds-metric-time-panel">
        <div className="ds-metric-time-main">{value}</div>
        <div className="ds-metric-time-rail"><span /><span /><span /><span /><span /></div>
      </div>
    );
  } else if (variant === 'online') {
    const raw = typeof value === 'string' ? value : String(value);
    const parts = raw.split('/').map(s => s.trim());
    const online = Number(parts[0] || 0);
    const total = Number(parts[1] || 0);
    const pct = total ? (online / total) * 100 : 0;
    body = (
      <div className="ds-metric-online-panel">
        <div className="ds-dashboard-topcard-value">{raw}</div>
        <div className="ds-metric-line"><div className="ds-metric-line-fill online" style={{ width: `${pct}%` }} /></div>
      </div>
    );
  } else if (variant === 'region') {
    body = (
      <div className="ds-metric-region-panel">
        <div className="ds-dashboard-topcard-value">{value}</div>
        <div className="ds-metric-region-dots"><span /><span /><span /><span /><span /></div>
      </div>
    );
  } else if (variant === 'traffic' || variant === 'speed') {
    body = value;
  } else if (variant === 'asset') {
    const txt = typeof value === 'string' ? value : String(value);
    const m = txt.match(/([¥$€£])\s*([\d,]+)(?:\.(\d{2}))?/);
    const integer = m ? m[2] : txt;
    const decimal = m && m[3] ? m[3] : '00';
    body = (
      <div className="ds-asset-topcard">
        <div className="ds-asset-topcard-money">
          <span className="ds-asset-topcard-symbol">¥</span>
          <span className="ds-asset-topcard-int">{integer}</span>
          <span className="ds-asset-topcard-dec">.{decimal}</span>
        </div>
      </div>
    );
  }

  return (
    <Card
      className={`ds-dashboard-topcard ds-dashboard-topcard-clickable ${active ? 'is-active' : ''} overflow-hidden border shadow-sm bg-card select-none`}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={`${title} 详情`}
      data-card-key={cardKey}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <CardContent className="ds-dashboard-topcard-content ds-dashboard-topcard-content-lefthead">
        <div className="ds-dashboard-topcard-head ds-dashboard-topcard-head-corner">
          <div className="ds-dashboard-topcard-icon">{icon}</div>
          <div className="ds-dashboard-topcard-title">{title}</div>
        </div>
        <div className="ds-dashboard-topcard-body">{body}</div>
        <div className="ds-dashboard-topcard-hint">点击查看详情</div>
      </CardContent>
    </Card>
  );
};

function TopCardDetailPanel({ title, icon, children, onClose }: { title: string; icon?: React.ReactNode; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="ds-topcard-detail-card">
      <div className="ds-topcard-detail-head">
        <div className="ds-topcard-detail-title"><span>{icon}</span><strong>{title}</strong></div>
        <button type="button" className="ds-topcard-detail-close" onClick={onClose}>收起</button>
      </div>
      <div className="ds-topcard-detail-body">{children}</div>
    </div>
  );
}

function TrafficPairMini({ up, down }: { up: string; down: string }) {
  return (
    <div className="ds-metric-traffic-panel" title={`上行 ${up} / 下行 ${down}`}>
      <div className="ds-metric-traffic-stack">
        <div className="ds-metric-traffic-lane up"><span className="tag">上行</span><strong>{up}</strong></div>
        <div className="ds-metric-traffic-lane down"><span className="tag">下行</span><strong>{down}</strong></div>
      </div>
    </div>
  );
}

function NetworkSpeedRings({ up, down }: { up: number; down: number }) {
  const softCap = Math.max(10 * 1024 * 1024, up, down);
  const upPct = softCap ? Math.max(0, Math.min(100, (up / softCap) * 100)) : 0;
  const downPct = softCap ? Math.max(0, Math.min(100, (down / softCap) * 100)) : 0;
  return (
    <div className="ds-speed-card-visual ds-speed-card-visual-interactive" title={`下载 ${formatSpeed(down)} / 上传 ${formatSpeed(up)}。圆环为相对活跃度，不代表带宽上限占用。`}>
      <div className="ds-speed-dual-ring">
        <svg viewBox="0 0 100 100" aria-hidden="true">
          <circle className="ds-speed-ring-track ds-speed-ring-track-down" cx="50" cy="50" r="42" />
          <circle className="ds-speed-ring-value ds-speed-ring-value-down" cx="50" cy="50" r="42" pathLength="100" strokeDasharray={`${downPct} 100`} />
          <circle className="ds-speed-ring-track ds-speed-ring-track-up" cx="50" cy="50" r="31" />
          <circle className="ds-speed-ring-value ds-speed-ring-value-up" cx="50" cy="50" r="31" pathLength="100" strokeDasharray={`${upPct} 100`} />
        </svg>
        <div className="ds-speed-ring-center">
          <strong>↓ {formatSpeedShort(down)}</strong>
          <span>↑ {formatSpeedShort(up)}</span>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, tone, onClick, active, interactiveValue, className }: { icon?: React.ReactNode; label: string; value: React.ReactNode; tone?: 'blue' | 'green' | 'orange' | 'purple'; onClick?: () => void; active?: boolean; interactiveValue?: boolean; className?: string }) {
  const cls = `ds-detail-mini-stat ds-detail-mini-stat-corner ${tone ? `tone-${tone}` : ''} ${onClick ? 'is-clickable' : ''} ${active ? 'is-active' : ''} ${interactiveValue ? 'is-value-interactive' : ''} ${className || ''}`;
  const inner = (
    <>
      <div className="ds-detail-mini-head">
        <div className="ds-detail-mini-icon">{icon}</div>
        <span>{label}</span>
      </div>
      <div className="ds-detail-mini-value"><strong>{value}</strong></div>
    </>
  );
  if (onClick) return <button type="button" className={cls} onClick={onClick}>{inner}</button>;
  return <div className={cls}>{inner}</div>;
}

function NameChips({ nodes, emptyText }: { nodes: any[]; emptyText: string }) {
  if (!nodes.length) return <div className="ds-detail-empty">{emptyText}</div>;
  return (
    <div className="ds-detail-chip-list">
      {nodes.map((node) => <span key={node.uuid} className="ds-detail-chip">{node.name}</span>)}
    </div>
  );
}

const TIME_ZONE_OPTIONS = [
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Seoul',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];


function getCountryCodeFromRegionFlag(region: string): string | null {
  const chars = Array.from(String(region || '').trim());
  if (chars.length !== 2) return null;
  const start = 0x1F1E6;
  const a = chars[0].codePointAt(0);
  const b = chars[1].codePointAt(0);
  if (a == null || b == null) return null;
  if (a < start || a > 0x1F1FF || b < start || b > 0x1F1FF) return null;
  return String.fromCodePoint(a - start + 0x41) + String.fromCodePoint(b - start + 0x41);
}

function getRegionDisplayName(region: string): string {
  const raw = String(region || '').trim();
  if (!raw) return 'UN';

  // Prefer ISO-3166 / regional abbreviations. This is algorithmic, not a short manual map:
  // 🇺🇸 -> US, 🇯🇵 -> JP, 🇭🇰 -> HK, etc.
  const flagMatch = raw.match(/[\u{1F1E6}-\u{1F1FF}]{2}/u)?.[0];
  const codeFromFlag = flagMatch ? getCountryCodeFromRegionFlag(flagMatch) : null;
  if (codeFromFlag) return codeFromFlag;

  if (/^[a-zA-Z]{2}$/.test(raw)) return raw.toUpperCase();

  // If a custom text value is stored in Komari, keep it rather than guessing.
  return raw;
}

function TimeZoneSelect({ value, onChange }: { value?: string; onChange: (value: string) => void }) {
  const current = value || 'Asia/Shanghai';
  return (
    <select className="ds-timezone-select" value={current} onChange={(e) => onChange(e.target.value)} aria-label="选择时区">
      {TIME_ZONE_OPTIONS.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
    </select>
  );
}

function TimeDetail() {
  const { themeConfig, setSelectedTimeZone } = useTheme();
  const selectedTimeZone = themeConfig.selectedTimeZone || 'Asia/Shanghai';
  return (
    <div className="ds-detail-grid ds-detail-grid-3">
      <MiniStat icon={<Clock size={15} />} label="当前时间" value={<CurrentTimeCard className="ds-time-detail-value" timeZone={selectedTimeZone} />} tone="blue" className="ds-time-detail-current" />
      <MiniStat icon={<Globe size={15} />} label="时区" value={<TimeZoneSelect value={selectedTimeZone} onChange={setSelectedTimeZone} />} tone="purple" interactiveValue />
      <MiniStat icon={<Activity size={15} />} label="刷新机制" value="5 秒自动刷新" tone="green" />
    </div>
  );
}

function OnlineDetail({ metrics, nodeScope, onScopeChange }: { metrics: DashboardMetrics; nodeScope: NodeScope; onScopeChange: (scope: NodeScope) => void }) {
  return (
    <div className="ds-detail-stack ds-detail-stack-compact">
      <div className="ds-detail-grid ds-detail-grid-3">
        <MiniStat icon={<Server size={15} />} label="全部" value={metrics.totalCount} tone="blue" active={nodeScope.kind === 'all'} onClick={() => onScopeChange({ kind: 'all' })} />
        <MiniStat icon={<Activity size={15} />} label="在线" value={metrics.onlineCount} tone="green" active={nodeScope.kind === 'online'} onClick={() => onScopeChange({ kind: 'online' })} />
        <MiniStat icon={<Gauge size={15} />} label="离线" value={metrics.offlineCount} tone="orange" active={nodeScope.kind === 'offline'} onClick={() => onScopeChange({ kind: 'offline' })} />
      </div>
    </div>
  );
}

function RegionDetail({ metrics, nodeScope, onScopeChange }: { metrics: DashboardMetrics; nodeScope: NodeScope; onScopeChange: (scope: NodeScope) => void }) {
  return (
    <div className="ds-detail-stack">
      <div className="ds-detail-grid ds-detail-grid-2">
        <MiniStat icon={<MapPinned size={15} />} label="点亮地区" value={metrics.activeRegionCount} tone="purple" />
        <MiniStat icon={<Activity size={15} />} label="在线节点" value={metrics.onlineCount} tone="green" active={nodeScope.kind === 'online'} onClick={() => onScopeChange({ kind: 'online' })} />
      </div>
      <div className="ds-detail-section-title">选择地区查看对应机器</div>
      <div className="ds-region-pill-grid">
        {metrics.regionCounts.length ? metrics.regionCounts.map((item) => (
          <button type="button" key={item.region} className={`ds-region-pill ${nodeScope.kind === 'region' && nodeScope.region === item.region ? 'is-active' : ''}`} onClick={() => onScopeChange({ kind: 'region', region: item.region })}>
            <span className="ds-region-pill-main"><span className="ds-region-pill-flag"><Flag flag={item.region} size="5" /></span><span className="ds-region-pill-name">{getRegionDisplayName(item.region)}</span></span><strong>{item.count}</strong>
          </button>
        )) : <div className="ds-detail-empty">暂无在线地区</div>}
      </div>
    </div>
  );
}

function TrafficDetail({ metrics }: { metrics: DashboardMetrics }) {
  return (
    <div className="ds-detail-grid ds-detail-grid-3">
      <MiniStat icon={<ArrowUp size={15} />} label="累计上行" value={formatBytes(metrics.trafficUp)} tone="orange" />
      <MiniStat icon={<ArrowDown size={15} />} label="累计下行" value={formatBytes(metrics.trafficDown)} tone="blue" />
      <MiniStat icon={<ArrowUpRight size={15} />} label="累计合计" value={formatBytes(metrics.trafficUp + metrics.trafficDown)} tone="purple" />
    </div>
  );
}

function SpeedDetail({ metrics }: { metrics: DashboardMetrics }) {
  return (
    <div className="ds-detail-stack">
      <div className="ds-speed-detail-layout">
        <NetworkSpeedRings up={metrics.speedUp} down={metrics.speedDown} />
        <div className="ds-detail-grid ds-detail-grid-2">
          <MiniStat icon={<ArrowDown size={15} />} label="实时下载" value={formatSpeed(metrics.speedDown)} tone="blue" />
          <MiniStat icon={<ArrowUp size={15} />} label="实时上传" value={formatSpeed(metrics.speedUp)} tone="orange" />
        </div>
      </div>
      <div className="ds-detail-section-title">速率 Top</div>
      <div className="ds-speed-node-list">
        {metrics.speedTopNodes.length ? metrics.speedTopNodes.map((node) => (
          <div key={node.uuid} className="ds-speed-node-row">
            <span>{node.name}</span>
            <strong><ArrowDown size={11} />{formatSpeed(node.down)}</strong>
            <strong><ArrowUp size={11} />{formatSpeed(node.up)}</strong>
          </div>
        )) : <div className="ds-detail-empty">当前无明显网络速率</div>}
      </div>
    </div>
  );
}

function AssetDetail({ metrics }: { metrics: DashboardMetrics }) {
  return (
    <div className="ds-detail-grid ds-detail-grid-3">
      <MiniStat icon={<WalletCards size={15} />} label="在线资产估值" value={`¥ ${metrics.assetTotal.toFixed(2)}`} tone="purple" />
      <MiniStat icon={<Server size={15} />} label="资产条目" value={metrics.assetCount || metrics.onlineCount} tone="blue" />
      <MiniStat icon={<Activity size={15} />} label="统计范围" value="在线节点" tone="green" />
    </div>
  );
}
